import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Auth0User, Profile, UserGroups } from "../../@types/data";
import { ProfileContext } from "../../context";

const E2E_ROLES = ["qa", "qc", "channelmanager"] as const;
type E2ERole = (typeof E2E_ROLES)[number];

const isE2ERole = (role: string | null): role is E2ERole =>
  // Widened because includes() only accepts the tuple's own literal types.
  (E2E_ROLES as readonly (string | null)[]).includes(role);

const emptyGroups = (): UserGroups => ({
  admin: false,
  qa: false,
  qaer: false,
  superuser: false,
  channelmanager: false,
  channelowner: false,
  outputreviewer: false,
  editor: false,
  editormanager: false,
  accountmanager: false,
  manager: false,
  voiceactor: false,
  casteditor: false
});

const createProfile = (role: E2ERole): Profile => {
  const groups = emptyGroups();
  groups.qa = true;
  if (role === "qc") {
    groups.outputreviewer = true;
  }
  // The e2e role that can edit channels, which the channel pages specs need.
  if (role === "channelmanager") {
    groups.channelmanager = true;
  }

  const name = `Katana ${role.toUpperCase()}`;
  return {
    binderUserId: `katana-e2e-${role}`,
    name,
    familyName: role.toUpperCase(),
    givenName: "Katana",
    picture: "",
    email: `katana-${role}@example.test`,
    sub: `katana-e2e-${role}`,
    canEdit: false,
    groups,
    initials: `K${role.charAt(0).toUpperCase()}`
  };
};

const createUserData = (profile: Profile): Auth0User => ({
  given_name: profile.givenName,
  family_name: profile.familyName,
  nickname: profile.givenName,
  name: profile.name,
  picture: profile.picture,
  locale: "en-GB",
  updated_at: "",
  email: profile.email,
  email_verified: true,
  sub: profile.sub,
  user_metadata: {}
});

export const E2EProfileProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string>();
  const token = useRef<string>("katana-e2e-token");

  useEffect(() => {
    if (window.location.hostname !== "localhost") {
      setError("Katana E2E authentication is restricted to localhost.");
      return;
    }

    const role = new URLSearchParams(window.location.search).get("e2eRole");
    if (!isE2ERole(role)) {
      setError(
        "Katana E2E authentication requires e2eRole=qa, e2eRole=qc or e2eRole=channelmanager."
      );
      return;
    }

    setProfile(createProfile(role));
  }, []);

  if (error) {
    throw new Error(error);
  }

  if (!profile) {
    return null;
  }

  return (
    <ProfileContext.Provider
      value={{
        profile,
        hasRole: true,
        setProfile,
        isAuthenticated: true,
        isLoading: false,
        isTokenLoading: false,
        authProvider: "auth0",
        token,
        logout: () => undefined,
        refreshTokenCallback: async () => undefined,
        userData: createUserData(profile),
        updateUserMetadata: () => undefined
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
